import { effect, inject, Injectable, signal, untracked } from '@angular/core';
import {
  type Notification,
  type NotificationFilter,
  NotificationsService,
} from './notifications';

export type NotificationListState = {
  notifications: Notification[];
  total: number;
  currentPage: number;
  lastPage: number;
  loading: boolean;
  loadingMore: boolean;
};

const PER_PAGE = 10;

const emptyState: NotificationListState = {
  notifications: [],
  total: 0,
  currentPage: 0,
  lastPage: 0,
  loading: false,
  loadingMore: false,
};

/** One filtered, paged list. Provide it on the component that shows it. */
@Injectable()
export class NotificationList {
  private notificationsService = inject(NotificationsService);

  private readonly _state = signal(emptyState);
  private readonly _filter = signal<NotificationFilter>({
    unreadOnly: false,
    type: null,
  });
  private request = 0;

  readonly state = this._state.asReadonly();
  readonly filter = this._filter.asReadonly();

  constructor() {
    effect(() => {
      this.notificationsService.changes();
      untracked(() => void this.reload());
    });
  }

  setFilter(filter: Partial<NotificationFilter>) {
    this._filter.update((f) => ({ ...f, ...filter }));
    this._state.set(emptyState);
    return this.reload();
  }

  reload() {
    return this.fetchPage(1, { replace: true });
  }

  async loadMore() {
    const state = this._state();
    if (state.loadingMore || state.currentPage >= state.lastPage) return;
    await this.fetchPage(state.currentPage + 1, { replace: false });
  }

  private async fetchPage(page: number, { replace }: { replace: boolean }) {
    const request = ++this.request;

    this._state.update((s) => ({
      ...s,
      loading: replace,
      loadingMore: !replace,
    }));

    try {
      const response = await this.notificationsService.fetch(
        this._filter(),
        page,
        PER_PAGE
      );
      // A newer request owns the list now.
      if (request !== this.request) return;

      this._state.update((s) => ({
        notifications: replace
          ? response.data
          : [...s.notifications, ...response.data],
        total: Number(response.metadata.total),
        currentPage: Number(response.metadata.currentPage),
        lastPage: Number(response.metadata.lastPage),
        loading: false,
        loadingMore: false,
      }));
    } catch {
      if (request !== this.request) return;
      this._state.update((s) => ({ ...s, loading: false, loadingMore: false }));
    }
  }
}
